<template>
  <div v-if="selectedPlan !== null && selectedPlan !== undefined">
    <v-dialog v-model="dialog" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ $t("globals.cancel_wizard") }}
        </v-card-title>
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="dialog = false">
            {{ $t("globals.keep_working") }}
          </v-btn>
          <v-btn color="primary" @click="cancel()">
            {{ $t("globals.cancel_anyway") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="warning" persistent max-width="400">
      <material-card
        v-if="warning"
        :title="$t('globals.warning')"
        icon="spellcheck"
        :color="$vuetify.theme.themes.light.warning"
      >
        <v-card-text>
          {{ warningMessage }}
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="warning = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            @click="
              warning = false;
              step = 2;
            "
          >
            {{ $t("globals.ok") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <div class="with-margin" v-if="term !== null">
      <material-card
        :title="
          $t(
            'views.eligibility_hour_bank_evaluation_wizard.evaluate_hour_bank_eligibility'
          )
        "
        :color="$vuetify.theme.themes.light.secondary"
        icon="play_circle"
      >
        <v-card-text>
          {{
            $t(
              "views.eligibility_hour_bank_evaluation_wizard.you_are_few_steps_away_from_calculating_eligibility_{0}",
              [trans(selectedPlan, "Name")]
            )
          }}
        </v-card-text>
        <v-stepper v-model="step" vertical>
          <v-stepper-step :complete="step > 1" step="1">
            {{
              $t("views.eligibility_hour_bank_evaluation_wizard.select_period")
            }}
            <small>
              {{
                $t(
                  "views.eligibility_hour_bank_evaluation_wizard.select_the_calendar_year_and_month_for_the_calculation"
                )
              }}
            </small>
          </v-stepper-step>
          <v-stepper-content step="1">
            <v-card>
              <v-card-text>
                <v-form v-model="valid">
                  <v-container fluid grid-list-md>
                    <v-row>
                      <v-col>
                        <no-autocomplete-textfield
                          :label="$t('headers.t4a.Year')"
                          v-model="item.CalendarYear"
                          :rules="[$rules.required, $rules.dynamic.length(4)]"
                          class="required"
                        ></no-autocomplete-textfield>
                      </v-col>
                      <v-col>
                        <trans-enum
                          :value.sync="item.CalendarMonth"
                          :items="options.Enums.Month"
                          :label="$t('components.duepicker.month')"
                          :rules="[$rules.required]"
                        ></trans-enum>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-flex>
                  <v-btn text @click="dialog = true">
                    {{ $t("globals.cancel") }}
                  </v-btn>
                  <v-btn
                    :loading="loading"
                    :disabled="!valid"
                    color="primary"
                    @click="step = 2"
                  >
                    {{ $t("globals.continue") }}
                  </v-btn>
                </v-flex>
              </v-card-actions>
            </v-card>
          </v-stepper-content>

          <v-stepper-step :complete="step > 2" step="2">
            {{ $t("views.eligibility_hour_bank_evaluation_wizard.evaluate") }}
            <small>
              {{
                $t(
                  "views.eligibility_hour_bank_evaluation_wizard.click_the_button_to_evaluate"
                )
              }}
            </small>
          </v-stepper-step>

          <v-stepper-content step="2">
            <v-card>
              <v-card-actions>
                <v-btn :loading="loading" color="success" @click="tryEvaluate">
                  {{
                    $t("views.eligibility_hour_bank_evaluation_wizard.evaluate")
                  }}
                </v-btn>
              </v-card-actions>
              <v-container fluid>
                <v-row>
                  <v-col>
                    <v-banner
                      v-if="success"
                      color="success"
                      icon="success"
                      elevation="0"
                      icon-color="white"
                    >
                      {{
                        $t(
                          "views.eligibility_hour_bank_evaluation_wizard.you_will_be_notified_by_email"
                        )
                      }}
                    </v-banner>
                    <v-banner
                      v-if="chainWarning"
                      color="warning"
                      icon="warning"
                      elevation="0"
                      icon-color="white"
                    >
                      {{
                        $t(
                          "views.eligibility_hour_bank_evaluation_wizard.chain_warning_{0}", [lastEvaluationYearMonth]
                        )
                      }}
                    </v-banner>
                  </v-col>
                </v-row>
              </v-container>
              <v-card-actions>
                <v-flex>
                  <v-btn
                    :disabled="success"
                    :loading="loading"
                    text
                    @click="step = 1"
                  >
                    {{ $t("globals.back") }}
                  </v-btn>
                  <v-btn
                    :loading="loading"
                    :disabled="!success"
                    color="primary"
                    @click="finish"
                  >
                    {{ $t("globals.finish") }}
                  </v-btn>
                </v-flex>
              </v-card-actions>
            </v-card>
          </v-stepper-content>
        </v-stepper>
      </material-card>
      <error-handler :error="error"></error-handler>
    </div>
  </div>
  <div v-else>
    <v-row justify="center">
      <v-progress-circular
        :size="70"
        :width="7"
        color="accent"
        indeterminate
      ></v-progress-circular>
    </v-row>
  </div>
</template>

<script>
import ErrorHandler from "../components/ErrorHandler";
import { locker } from "../mixins/locker";
import { translate2 } from "../trans2";
import TransEnum from "../components/TransEnum";
import { planSetter } from "../mixins/planSetter";
import NoAutocompleteTextfield from "../components/NoAutocompleteTextfield";

import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    ErrorHandler,
    TransEnum,
    NoAutocompleteTextfield,
  },
  mixins: [locker, planSetter],
  data() {
    return {
      lastEvaluationYearMonth: null,
      chainWarning: false,
      valid: false,
      error: null,
      loading: false,
      saving: false,
      dialog: false,
      step: 1,
      item: {
        CalendarMonth: null,
        CalendarYear: null,
      },
      warningMessage: "",
      success: false,
      // coverageEffectiveYearRule: (v) => {
      //     if (v === undefined || v === null) {
      //       return true;
      //     }
      //     if (
      //       this.planEntryDate === null ||
      //       this.planEntryDate === undefined
      //     ) {
      //       return true;
      //     }
      //     const planEntryYear = new Date(
      //       Date.parse(this.planEntryDate)
      //     ).getFullYear();
      //     return (
      //       v >= planEntryYear ||
      //       this.$t("validation.year_equal_to_or_greater_than_{0}", [
      //         planEntryYear,
      //       ])
      //     );
      //   },
    };
  },
  computed: {
    ...mapGetters("common", ["language"]),
    ...mapGetters("plansMembers", ["options"]),
    ...mapGetters("auth", ["user"]),
    ...mapGetters("common", { selectedPlan: "plan" }),
  },
  watch: {
    "item.CalendarMonth": {
      handler(v) {
        if (
          this.item.CalendarYear !== null &&
          this.item.CalendarYear !== undefined
        ) {
          this.getEvaluationsForMonth();
        }
      },
    },
    "item.CalendarYear": {
      handler(v) {
        if (
          this.item.CalendarMonth !== null &&
          this.item.CalendarMonth !== undefined
        ) {
          this.getEvaluationsForMonth();
        }
      },
    },
  },
  methods: {
    trans(item, field) {
      return translate2(item, field);
    },
    ...mapActions("plansMembers", [
      "evaluateEligibilityHourBank",
      "getEligibilityBankEvaluations",
      "eligibilityBankReevaluate",
    ]),
    async getEvaluationsForMonth() {
      try {
        this.loading = true;
        console.log(this.selectedPlan.Id);
        this.existingEvaluations = (
          await this.getEligibilityBankEvaluations({
            planIdentityId: this.selectedPlan.Id,
            calendarYear: this.item.CalendarYear,
            calendarMonth:
              this.options.Enums.Month.indexOf(this.item.CalendarMonth) + 1,
          })
        ).data.value;
        if (this.existingEvaluations.length > 0) {
          this.warningMessage = this.$t(
            "views.eligibility_hour_bank_evaluation_wizard.warning_exists"
          );
        } else {
          this.warningMessage = this.$t(
            "views.eligibility_hour_bank_evaluation_wizard.warning_not_exists"
          );
        }
        this.warning = true;
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    finish() {
      this.$router.push({
        name: "home",
        params: { planId: this.selectedPlan.Id },
      });
    },
    async tryEvaluate() {
      this.loading = true;
      try {
        if (
          this.warningMessage ==
          this.$t(
            "views.eligibility_hour_bank_evaluation_wizard.warning_not_exists"
          )
        ) {
         this.lastEvaluationYearMonth = (await this.evaluateEligibilityHourBank({
            planIdentityId: this.selectedPlan.Id,
            calendarYear: this.item.CalendarYear,
            calendarMonth:
              this.options.Enums.Month.indexOf(this.item.CalendarMonth) + 1,
          })).data;
          console.log("response is: ", this.lastEvaluationYearMonth);
          this.success = true;
          if (this.lastEvaluationYearMonth.length > 0) {
            this.chainWarning = true;
          }
        } else {
          await this.eligibilityBankReevaluate({
            planIdentityId: this.selectedPlan.Id,
            calendarYear: this.item.CalendarYear,
            calendarMonth:
              this.options.Enums.Month.indexOf(this.item.CalendarMonth) + 1,
          });
          this.success = true;
        }
      } catch (error) {
        this.success = false;
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.dialog = false;
      this.$router.push({
        name: "home",
        params: { planId: this.selectedPlan.Id },
      });
    },
  },
};
</script>

<style>
.with-margin {
  margin: 20px;
}
.background-red {
  background-color: red;
}
.background-green {
  background-color: green;
  color: white;
}
.v-stepper small {
  color: rgba(0, 0, 0, 0.54);
}
</style>
